/**
 * Facet helpers — reading `publicData` values that describe fitment.
 *
 * A "facet" here is one stored value that says something about what a
 * listing fits: the car make, the car model, the rim diameter, the bolt
 * circle, the tyre size, the season.
 *
 * Two traps in the stored data drive everything in this file.
 *
 * 1. Placeholder values. Several enum fields offer an "other" option and
 *    the bulk-upload path writes the literal string "blank" when the
 *    seller left a car brand empty. Both are stored values, so a plain
 *    truthiness check says "present" — but neither proves anything about
 *    fitment, and neither should be shown as a breadcrumb level. They
 *    have to be read as absent.
 *
 * 2. Inconsistent formatting of the same value. The bolt circle 114.3 is
 *    stored as "114.3" on rims and as "114_3" elsewhere; category ids are
 *    a mix of "volvo", "Volvo" and "VW"; sizes carry stray quotes and
 *    trailing zeros ("20.00"). Comparing raw strings therefore reports
 *    "different" for values that are the same measurement.
 */

/* Stored values that mean "the seller did not answer". Compared
 * lower-cased and trimmed. "blank" comes from the bulk-upload/feed path;
 * "other" is the escape option on several Console enums. */
export const PLACEHOLDER_FACET_VALUES = [
  'other',
  'blank',
  'none',
  'null',
  'undefined',
  'n/a',
  '-',
  '',
];

const asString = value =>
  value === null || value === undefined ? '' : String(value).trim();

/**
 * True when a stored value can be used as a fitment facet or a breadcrumb
 * level. False for missing values and for the placeholders above.
 */
export const isUsableFacetValue = value => {
  const str = asString(value);
  return str !== '' && !PLACEHOLDER_FACET_VALUES.includes(str.toLowerCase());
};

/**
 * Normalise a measurement so two spellings of the same number compare
 * equal: "114_3" and "114.3" → "114.3", "20.00" and 20 → "20",
 * '17"' → "17". Non-numeric values fall back to a lower-cased string.
 */
export const normalizeFacetValue = value => {
  const str = asString(value)
    .replace(/"/g, '')
    .replace(/_/g, '.')
    .replace(/,/g, '.');
  const num = Number(str);
  return Number.isFinite(num) && str !== '' ? String(num) : str.toLowerCase();
};

/**
 * Normalise a category id (car make / car model). Ids are inconsistently
 * cased and occasionally spaced or hyphenated — "VW"/"vw",
 * "land rover"/"landrover", "3series"/"3-series" are the same category.
 * Swedish letters are kept because some ids use them.
 */
export const normalizeCategoryId = value =>
  asString(value)
    .toLowerCase()
    .replace(/[^a-z0-9åäö]/g, '');

/** Case- and spacing-insensitive category lookup. Exact id wins. */
export const findCategoryById = (categories, id) => {
  if (!Array.isArray(categories) || !isUsableFacetValue(id)) return null;
  const exact = categories.find(category => category?.id === id);
  if (exact) return exact;
  const target = normalizeCategoryId(id);
  return categories.find(category => normalizeCategoryId(category?.id) === target) || null;
};
